import type { CampaignsLeadBody } from '@/services/campaignsClient';
import type { WregIntakeForm } from '@/components/webreg/screens/wregIntakeFields';
import { toCmsGender } from '@/components/webreg/wregCmsPoll';
import { isoBirthDateIsInRange, toIsoBirthDate } from '@/utils/date';
import { emailIsValid } from '@/utils/email';
import { mbiIsValid, normalizeMbi } from '@/utils/mbi';
import { toE164 } from '@/utils/phone';
import { UTM_PARAMS, type UTMParams } from '@/utils/utm';

export interface CampaignsAttribution {
  webregUserId?: string;
  utmParams?: UTMParams | null;
  promoCode?: string;
}

const NAME_MAX = 100;
const UTM_MAX = 255;
const ID_MAX = 64;

const clip = (value: unknown, max: number): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().slice(0, max);
  return trimmed === '' ? undefined : trimmed;
};

// An identifier cut short is a different identifier, so an over-long one is dropped.
const fitted = (value: unknown, max: number): string | undefined => {
  const trimmed = clip(value, max + 1);
  return trimmed !== undefined && trimmed.length <= max ? trimmed : undefined;
};

const SPECIAL_USE_TLDS = new Set(['arpa', 'invalid', 'local', 'localhost', 'onion', 'test']);

// The server's EmailStr refuses special-use TLDs and malformed punycode. Any xn-- label is left out
// rather than decoded, since a wrong guess costs the whole snapshot.
const serverAcceptsEmail = (email: string): boolean => {
  const labels = email.slice(email.lastIndexOf('@') + 1).toLowerCase().split('.');
  return !SPECIAL_USE_TLDS.has(labels[labels.length - 1]) && !labels.some((l) => l.startsWith('xn--'));
};

// A value the server would reject is left out rather than sent: the snapshot is cumulative, so one
// bad field (a half-typed email) would 422 the request and drop every other field with it.
const validDob = (dob: string): string | undefined => {
  const iso = toIsoBirthDate(dob);
  return iso !== '' && isoBirthDateIsInRange(iso) ? iso : undefined;
};

export const buildLeadSnapshot = (
  form: WregIntakeForm,
  attribution: CampaignsAttribution,
): CampaignsLeadBody => {
  const email = form.email.trim();
  const body: CampaignsLeadBody = {
    firstName: clip(form.first, NAME_MAX),
    lastName: clip(form.last, NAME_MAX),
    email: emailIsValid(email) && serverAcceptsEmail(email) ? email : undefined,
    phone: toE164(form.phone) ?? undefined,
    dateOfBirth: validDob(form.dob),
    gender: form.gender.trim() === '' ? undefined : toCmsGender(form.gender),
    mbi: mbiIsValid(form.mbi) ? normalizeMbi(form.mbi) : undefined,
    tcAccepted: form.tcAccepted ? true : undefined,
    webregUserId: fitted(attribution.webregUserId, ID_MAX),
    promoCode: fitted(attribution.promoCode, ID_MAX),
  };
  // Allow-listed: utmParams is read back from localStorage, and an unknown key is a 422.
  for (const key of Object.values(UTM_PARAMS)) {
    const clipped = clip(attribution.utmParams?.[key], UTM_MAX);
    if (clipped !== undefined) body[key] = clipped;
  }
  return Object.fromEntries(
    Object.entries(body).filter(([, value]) => value !== undefined),
  ) as CampaignsLeadBody;
};
